import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  acceptAllCookies,
  getCookieConsent,
  onConsentChange,
  rejectAllCookies,
  saveCookieConsent,
} from "@/lib/cookies";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) setVisible(true);
    else {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
    return onConsentChange((c) => {
      if (!c) {
        setVisible(true);
        setAnalytics(false);
        setMarketing(false);
      } else {
        setVisible(false);
        setAnalytics(c.analytics);
        setMarketing(c.marketing);
      }
    });
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setVisible(false);
  };
  const handleRejectAll = () => {
    rejectAllCookies();
    setVisible(false);
  };
  const handleSave = () => {
    saveCookieConsent({ analytics, marketing });
    setSettingsOpen(false);
    setVisible(false);
  };

  if (!visible && !settingsOpen) return null;

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 animate-in slide-in-from-bottom duration-300"
        >
          <div className="container max-w-5xl">
            <div className="bg-card border border-border rounded-2xl shadow-2xl p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="hidden md:flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Cookie className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading font-semibold text-foreground-strong text-base mb-1">
                    Usamos cookies
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Utilizamos cookies propias y de terceros (Google Analytics y
                    Meta) para analizar el uso del sitio y mejorar nuestros
                    servicios. Puedes aceptar todas, rechazarlas o configurarlas.
                    Más información en nuestra{" "}
                    <Link to="/legal/cookies" className="text-primary underline">
                      Política de Cookies
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 md:shrink-0">
                  <Button variant="outline" size="sm" onClick={handleRejectAll}>
                    Rechazar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSettingsOpen(true)}
                  >
                    Configurar
                  </Button>
                  <Button size="sm" onClick={handleAcceptAll}>
                    Aceptar todas
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Preferencias de cookies
            </DialogTitle>
            <DialogDescription>
              Activa las categorías que quieras permitir. Puedes cambiarlo en
              cualquier momento desde la Política de Cookies.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-xl">
              <div>
                <p className="font-semibold text-foreground-strong text-sm">
                  Necesarias
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Imprescindibles para el funcionamiento del sitio. No pueden
                  desactivarse.
                </p>
              </div>
              <Switch checked disabled />
            </div>

            <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-xl">
              <div>
                <p className="font-semibold text-foreground-strong text-sm">
                  Analítica (Google Analytics)
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Nos ayudan a entender cómo se usa la web para mejorarla.
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>

            <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-xl">
              <div>
                <p className="font-semibold text-foreground-strong text-sm">
                  Marketing (Meta Pixel)
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Para medir campañas y mostrar anuncios relevantes.
                </p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleRejectAll}>
              Rechazar todas
            </Button>
            <Button onClick={handleSave}>Guardar preferencias</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieBanner;
